<template>
    <div class="CMentorMap relative">
        <div ref="mapContainer" class="w-full h-full rounded-lg overflow-hidden" :style="{ minHeight: height }" />
        <!-- Loading overlay -->
        <div
            v-if="isLoading"
            class="absolute inset-0 z-[1000] flex items-center justify-center rounded-lg bg-gray-100/80 dark:bg-surface-card/80"
        >
            <div class="flex flex-col items-center gap-4">
                <div class="size-12 animate-spin rounded-full border-4 border-[#047274]/20 border-t-[#047274]" />
                <span class="text-sm text-[#6c5d73] dark:text-txt-body">Načítavam mapu...</span>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
interface MemberData {
    id: string
    name: string
    profileImg?: string
    link: string
}

interface LocationCount {
    city: string
    count: number
    country?: string
    region?: string
    lat?: number
    lng?: number
    members?: MemberData[]
}

interface CountLabelForms {
    one: string
    few: string
    many: string
}

interface CityCoords {
    city: string
    country?: string
    region?: string
    lat: number
    lng: number
    count: number
    members?: MemberData[]
}

interface Props {
    locations?: LocationCount[]
    selectedCity?: string | null
    height?: string
    userCity?: string | null
    countLabelForms?: CountLabelForms
}

const props = withDefaults(defineProps<Props>(), {
    locations: () => [],
    selectedCity: null,
    height: '300px',
    userCity: null,
    countLabelForms: () => ({
        one: 'člen',
        few: 'členovia',
        many: 'členov'
    })
})

const emit = defineEmits<{
    (e: 'city-click', city: string, location: CityCoords): void
}>()

const mapContainer = ref<HTMLElement | null>(null)
const map = ref<any>(null)
const L = ref<any>(null)
const markers = ref<any[]>([])
const isLoading = ref(true)
const cityCoords = ref<CityCoords[]>([])

// Center on Europe (where most community members are)
const MAP_CENTER = { lat: 48.5, lng: 17 }
const DEFAULT_ZOOM = 4
const MIN_ZOOM = 2

// Load Leaflet from CDN
async function loadLeaflet(): Promise<any> {
    if (typeof window === 'undefined') return null

    // Check if already loaded
    if ((window as any).L) {
        return (window as any).L
    }

    // Load CSS
    if (!document.querySelector('link[href*="leaflet.css"]')) {
        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
        link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY='
        link.crossOrigin = ''
        document.head.appendChild(link)
    }

    // Load JS
    return new Promise((resolve, reject) => {
        if ((window as any).L) {
            resolve((window as any).L)
            return
        }

        const script = document.createElement('script')
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'
        script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo='
        script.crossOrigin = ''
        script.onload = () => {
            resolve((window as any).L)
        }
        script.onerror = reject
        document.head.appendChild(script)
    })
}

// Create popup HTML for members
function createMembersPopupHtml(city: CityCoords): string {
    const members = city.members || []
    const maxDisplay = 6
    const displayMembers = members.slice(0, maxDisplay)
    const remaining = members.length - maxDisplay

    const memberCards = displayMembers.map(member => {
        const initials = member.name.split(' ').map(n => n[0]).join('').slice(0, 2)
        const avatar = member.profileImg
            ? `<img src="${member.profileImg}" alt="${member.name}" class="size-10 rounded-full object-cover" />`
            : `<div class="size-10 rounded-full bg-[#047274]/20 flex items-center justify-center text-[#047274] font-semibold text-sm">${initials}</div>`

        return `
            <a href="${member.link}" class="member-popup-item flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 hover:dark:bg-subtle transition-colors">
                ${avatar}
                <span class="text-sm font-medium text-gray-900 dark:text-txt-headings truncate max-w-[120px]">${member.name}</span>
            </a>
        `
    }).join('')

    const moreText = remaining > 0 ? `<div class="text-xs text-gray-500 dark:text-txt-body text-center pt-2 border-t">+${remaining} ďalších</div>` : ''

    return `
        <div class="member-popup">
            <div class="font-semibold text-base mb-2 text-gray-900 dark:text-txt-headings">${city.city}</div>
            <div class="member-popup-list flex flex-col gap-1 max-h-[240px] overflow-y-auto">
                ${memberCards}
            </div>
            ${moreText}
        </div>
    `
}

// Create custom circle marker for city
function createCityMarker(city: CityCoords, isSelected: boolean): any {
    if (!L.value) return null

    const radius = getMarkerRadius(city.count)
    const color = isSelected ? '#1e40af' : '#047274'
    const borderColor = isSelected ? '#1e3a8a' : '#035e60'

    const marker = L.value.circleMarker([city.lat, city.lng], {
        radius,
        fillColor: color,
        color: borderColor,
        weight: isSelected ? 3 : 2,
        opacity: 1,
        fillOpacity: 0.9
    })

    // Create tooltip with city name and count (shown on hover)
    marker.bindTooltip(`
        <div class="text-center">
            <div class="font-semibold">${city.city}</div>
            <div class="text-xs">${city.count} ${getCountLabel(city.count)}</div>
        </div>
    `, {
        permanent: false,
        direction: 'top',
        offset: [0, -radius]
    })

    // Create popup with member cards (shown on click)
    const hasMembers = city.members && city.members.length > 0
    if (hasMembers) {
        marker.bindPopup(createMembersPopupHtml(city), {
            maxWidth: 280,
            minWidth: 200,
            className: 'member-popup-container'
        })
    }

    // Click handler - only emit if no popup (popup handles its own interaction)
    marker.on('click', () => {
        if (!hasMembers) {
            emit('city-click', city.city, city)
        }
    })

    return marker
}

// Get marker radius based on count
function getMarkerRadius(count: number): number {
    if (count === 0) return 6
    if (count <= 2) return 8
    if (count <= 5) return 10
    if (count <= 10) return 12
    if (count <= 20) return 14
    return 16
}

function getCountLabel(count: number): string {
    if (count === 1) return props.countLabelForms.one
    if (count > 1 && count < 5) return props.countLabelForms.few
    return props.countLabelForms.many
}

// Geocode cities via API
async function geocodeCities(locations: LocationCount[]): Promise<CityCoords[]> {
    if (!locations.length) return []

    // Create a map for quick lookup by city+country (case-insensitive)
    const locationMap = new Map<string, LocationCount>()
    locations.forEach(l => {
        const key = `${l.city.toLowerCase()}|${(l.country || 'Slovakia').toLowerCase()}`
        locationMap.set(key, l)
    })

    const locationsWithCoordinates = locations
        .filter(l => typeof l.lat === 'number' && typeof l.lng === 'number')
        .map(l => ({
            city: l.city,
            country: l.country || 'Slovakia',
            region: l.region,
            lat: l.lat!,
            lng: l.lng!,
            count: l.count,
            members: l.members || []
        }))

    const locationsToGeocode = locations.filter(l => typeof l.lat !== 'number' || typeof l.lng !== 'number')

    if (!locationsToGeocode.length) return locationsWithCoordinates

    try {
        const cities = locationsToGeocode.map(l => ({
            city: l.city,
            country: l.country || 'Slovakia'
        }))

        const results = await $fetch<Array<{
            city: string
            country: string
            lat: number | null
            lng: number | null
        }>>('/api/cities/geocode', {
            method: 'POST',
            body: { cities }
        })

        const geocodedLocations = results
            .filter(r => r.lat !== null && r.lng !== null)
            .map(r => {
                // Lookup by city+country
                const key = `${r.city.toLowerCase()}|${r.country.toLowerCase()}`
                const loc = locationMap.get(key)
                return {
                    city: r.city,
                    country: r.country,
                    region: loc?.region,
                    lat: r.lat!,
                    lng: r.lng!,
                    count: loc?.count || 0,
                    members: loc?.members || []
                }
            })

        return [...locationsWithCoordinates, ...geocodedLocations]
    } catch (error) {
        console.error('Failed to geocode cities:', error)
        return locationsWithCoordinates
    }
}

// Update markers on the map
function updateMarkers() {
    if (!map.value || !L.value) return

    // Remove existing markers
    markers.value.forEach(marker => {
        map.value.removeLayer(marker)
    })
    markers.value = []

    // Add new markers
    cityCoords.value.forEach(city => {
        const isSelected = props.selectedCity === city.city
        const marker = createCityMarker(city, isSelected)
        if (marker) {
            marker.addTo(map.value)
            markers.value.push(marker)
        }
    })
}

// Initialize the map
async function initMap() {
    if (!mapContainer.value) return

    try {
        L.value = await loadLeaflet()
        if (!L.value) return

        // Create map
        map.value = L.value.map(mapContainer.value, {
            scrollWheelZoom: true,
            zoomControl: true,
            minZoom: MIN_ZOOM
        }).setView([MAP_CENTER.lat, MAP_CENTER.lng], DEFAULT_ZOOM)

        // Add OpenStreetMap tiles
        L.value.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
            maxZoom: 18
        }).addTo(map.value)

        // Initial markers
        updateMarkers()
    } catch (error) {
        console.error('Failed to initialize map:', error)
    }
}

// Watch for location changes (including member data)
watch(() => props.locations, async (newLocations) => {
    if (newLocations && newLocations.length > 0 && map.value) {
        isLoading.value = true
        cityCoords.value = await geocodeCities(newLocations)
        updateMarkers()
        isLoading.value = false
    }
}, { deep: true, immediate: true })

// Watch for selected city changes
watch(() => props.selectedCity, () => {
    updateMarkers()
})

// Lifecycle
onMounted(async () => {
    // Keep the overlay up until the markers are placed — geocoding takes a while after Leaflet
    // loads, and an empty map in the meantime reads as "no members"
    try {
        await initMap()
        if (props.locations.length > 0) {
            cityCoords.value = await geocodeCities(props.locations)
            updateMarkers()
        }
    } finally {
        isLoading.value = false
    }
})

onBeforeUnmount(() => {
    if (map.value) {
        map.value.remove()
        map.value = null
    }
})
</script>

<style>
.CMentorMap .leaflet-container {
    z-index: 0;
    font-family: inherit;
}

.CMentorMap .leaflet-tooltip {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 4px 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.CMentorMap .leaflet-tooltip-top:before {
    border-top-color: #e5e7eb;
}

/* Member popup styles */
.CMentorMap .member-popup-container .leaflet-popup-content-wrapper {
    border-radius: 12px;
    padding: 0;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.CMentorMap .member-popup-container .leaflet-popup-content {
    margin: 12px;
    margin-right: 24px;
}

.CMentorMap .member-popup-container .leaflet-popup-close-button {
    top: 8px !important;
    right: 8px !important;
    width: 20px;
    height: 20px;
    font-size: 18px;
    color: #6c5d73;
}

.CMentorMap .member-popup-container .leaflet-popup-close-button:hover {
    color: #1d191f;
}

.CMentorMap .member-popup-container .leaflet-popup-tip {
    background: white;
}

.CMentorMap .member-popup-list::-webkit-scrollbar {
    width: 4px;
}

.CMentorMap .member-popup-list::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 2px;
}

.CMentorMap .member-popup-list::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 2px;
}

.CMentorMap .member-popup-item {
    text-decoration: none;
}

/* Neutral surfaces follow the selected theme; brand colors and images retain their colors. */
[data-theme="dark"] .CMentorMap .leaflet-tooltip {
    background: var(--color-surface-card);
    border: 1px solid var(--color-border)
}
[data-theme="dark"] .CMentorMap .leaflet-tooltip-top:before {
    border-top-color: var(--color-border)
}
[data-theme="dark"] .CMentorMap .member-popup-container .leaflet-popup-close-button:hover {
    color: var(--color-txt-body)
}
[data-theme="dark"] .CMentorMap .member-popup-container .leaflet-popup-tip {
    background: var(--color-surface-card)
}
</style>
